import { Box, Typography, useTheme, Card, CardContent, Chip, Grid, FormControl, FormGroup, FormControlLabel, Checkbox, Button, TextField, InputAdornment, IconButton, Dialog, DialogTitle, DialogContent, Select, MenuItem, Stack, Divider, Tooltip, Popover, Link } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { TabularIcon, ImagingClassificationIcon, ImagingSegmentationIcon } from '../../components/TaskTypeIcons';
import AppsIcon from '@mui/icons-material/Apps';
import { tokens } from "../../theme";
import { LoadingState, EmptyState } from "../../components/states";
import { useAppContext } from "../../contexts/AppContext";
import DatasetGroupCard from "./components/DatasetGroupCard";
import { groupCapabilitiesByDatasetRef } from "../../data/mockSites";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useMemo } from "react";
import AnalyticsIcon from '@mui/icons-material/Analytics';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined';
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';
import BusinessIcon from '@mui/icons-material/Business';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import ScienceIcon from '@mui/icons-material/Science';
import SecurityIcon from '@mui/icons-material/Security';
import GavelIcon from '@mui/icons-material/Gavel';
import HistoryIcon from '@mui/icons-material/History';
import HubIcon from '@mui/icons-material/Hub';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FactoryIcon from '@mui/icons-material/Factory';
import HeaderWithControls from "../../components/HeaderWithControls";
import PageToolbar from "../../components/PageToolbar";
import VisualizationModal from './components/VisualizationModal';
import UploadDatasetModal from '../../modals/UploadDatasetModal';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import StorageIcon from '@mui/icons-material/Storage';
// Sidebar uses DatasetOutlinedIcon for the Data Hub route — mirror it here so
// the page header matches the sidebar entry.
import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined';

//  OMOPInfo Tooltip Content
const OmopInfoCard = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    
    return (
        <Box sx={{ p: 2, maxWidth: 320 }}>
            <Typography variant="subtitle2" sx={{ color: colors.secondary[500], fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <VerifiedUserOutlinedIcon fontSize="small" /> Standardized Schema
            </Typography>
            <Typography variant="body2" sx={{ color: colors.grey[100], mb: 1 }}>
                Datasets are validated against a common data schema for semantic consistency and interoperability across healthcare institutions.
            </Typography>
            <Typography variant="caption" sx={{ color: colors.grey[300], display: 'block' }}>
                Currently based on the OMOP Common Data Model (CDM), with support for additional data formats planned in future releases.
            </Typography>
        </Box>
    );
};

// Dataset Card Component
const DatasetCard = ({ dataset, colors, handleOpenModal }) => {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  
  const isMaintenance = dataset.type === 'Imaging' || dataset.type === 'Segmentation';

  const handleSelect = () => {
      console.log("DatasetCard: handleSelect called for", dataset.name, "ID:", dataset.id);
      if (!isMaintenance) {
          console.log("DatasetCard: Navigating to /configs with state", { dataset_path: dataset.id });
          navigate('/configs', { state: { dataset_path: dataset.id } });
      }
  };

  return (
    <Card sx={{ 
      height: '100%', 
      display: 'flex', 
      backgroundColor: 'var(--fx-paper)',
      flexDirection: 'column',
      border: '1px solid var(--fx-border)',
      borderRadius: 2,
      boxShadow: 'var(--fx-shadow-sm)',
      transition: 'all 0.2s ease',
      '&:hover': {
        boxShadow: 'var(--fx-shadow-lg)'
      },
      overflow: 'visible'
    }}>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
        {/* Card Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ pr: 2 }}>
            <Typography variant="h6" sx={{ color: 'var(--fx-text-strong)', fontWeight: 700, lineHeight: 1.2, mb: 1, fontSize: '1.1rem' }}>
              {dataset.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Tooltip title="Professionally verified for quality.">
                    <Box sx={{ 
                    bgcolor: 'var(--fx-success-soft)',
                    color: 'var(--fx-success-text)',
                    borderRadius: '100px',
                    px: 1, py: 0.5,
                    display: 'flex', alignItems: 'center', gap: 0.5,
                    cursor: 'help'
                    }}>
                        <VerifiedUserOutlinedIcon sx={{ fontSize: 12 }} /> 
                        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '10px', lineHeight: 1 }}>
                        {dataset.quality}
                        </Typography>
                    </Box>
                </Tooltip>
                
            </Box>
            

            {/* Discovery Tags Row */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                {/* Privacy Badge */}
                <Tooltip title="Data Privacy Level">
                    <Box sx={{ 
                        display: 'flex', alignItems: 'center', gap: 0.5,
                        bgcolor: dataset.privacy === 'Synthetic' ? 'var(--fx-success-soft)' : dataset.privacy === 'Public' ? 'var(--fx-subtle)' : 'var(--fx-danger-soft)',
                        color: dataset.privacy === 'Synthetic' ? 'var(--fx-success-text)' : dataset.privacy === 'Public' ? 'var(--fx-text-secondary)' : 'var(--fx-danger-text)',
                        border: `1px solid ${dataset.privacy === 'Synthetic' ? 'var(--fx-success-border)' : dataset.privacy === 'Public' ? 'var(--fx-border)' : 'var(--fx-danger-border)'}`,
                        px: 0.8, py: 0.2, borderRadius: '6px'
                    }}>
                        <SecurityIcon sx={{ fontSize: 12 }} />
                        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase' }}>
                            {dataset.privacy}
                        </Typography>
                    </Box>
                </Tooltip>

                {/* REB Badge */}
                {dataset.reb && (
                    <Tooltip title="Research Ethics Board Approved">
                         <Box sx={{ 
                            display: 'flex', alignItems: 'center', gap: 0.5,
                            bgcolor: 'var(--fx-warning-soft)', color: 'var(--fx-warning-text)',
                            border: '1px solid var(--fx-warning-border)',
                            px: 0.8, py: 0.2, borderRadius: '6px'
                        }}>
                            <GavelIcon sx={{ fontSize: 12 }} />
                            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem' }}>
                                REB Approved
                            </Typography>
                        </Box>
                    </Tooltip>
                )}

                {/* Institution Type */}
                <Tooltip title="Data Source Type">
                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 0.5,
                        bgcolor: 'var(--fx-info-soft)', color: 'var(--fx-info-text)',
                        border: '1px solid var(--fx-info-border)',
                        px: 0.8, py: 0.2, borderRadius: '6px'
                    }}>
                        {dataset.institution === 'Hospital' ? <LocalHospitalIcon sx={{ fontSize: 12 }} /> :
                         dataset.institution === 'Industry' ? <FactoryIcon sx={{ fontSize: 12 }} /> :
                         <ScienceIcon sx={{ fontSize: 12 }} />}
                        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem' }}>
                            {dataset.institution}
                        </Typography>
                    </Box>
                </Tooltip>
            </Box>

            {/* F1.7 prototype — Site-local capability context row.
                Renders only when /datasets is driven by SiteDatasetCapability
                objects (mock mode). Communicates that the dataset is owned by
                a site, not a floating global object. */}
            {dataset.owning_site_display_name && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.25, flexWrap: 'wrap' }}>
                <Tooltip title="Owning federation site">
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 0.5,
                    bgcolor: 'var(--fx-info-soft)', color: 'var(--fx-info-text)',
                    border: '1px solid #bfdbfe',
                    px: 0.8, py: 0.2, borderRadius: '6px',
                  }}>
                    <HubIcon sx={{ fontSize: 12 }} />
                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem' }}>
                      {dataset.owning_site_display_name}
                    </Typography>
                  </Box>
                </Tooltip>
                {dataset.readiness_status && dataset.readiness_status !== 'ready' && (
                  <Tooltip title={`Capability readiness: ${dataset.readiness_status}`}>
                    <Box sx={{
                      display: 'flex', alignItems: 'center', gap: 0.5,
                      bgcolor: 'var(--fx-warning-soft)', color: 'var(--fx-warning-text)',
                      border: '1px solid var(--fx-warning-border)',
                      px: 0.8, py: 0.2, borderRadius: '6px',
                    }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase' }}>
                        {String(dataset.readiness_status).replace('_', ' ')}
                      </Typography>
                    </Box>
                  </Tooltip>
                )}
                {dataset.access_scope === 'denied' && (
                  <Tooltip title="Access required - contact your administrator">
                    <Box sx={{
                      display: 'flex', alignItems: 'center', gap: 0.5,
                      bgcolor: 'var(--fx-danger-soft)', color: 'var(--fx-danger-text)',
                      border: '1px solid var(--fx-danger-border)',
                      px: 0.8, py: 0.2, borderRadius: '6px',
                    }}>
                      <LockIcon sx={{ fontSize: 12 }} />
                      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem' }}>
                        Access required
                      </Typography>
                    </Box>
                  </Tooltip>
                )}
                {dataset.capability_id && (
                  <Tooltip title={`dataset_ref: ${dataset.dataset_ref || 'Not set'}`}>
                    <Typography variant="caption" sx={{ color: 'var(--fx-text-muted)', fontFamily: 'monospace', fontSize: '0.65rem' }}>
                      {dataset.capability_id}
                    </Typography>
                  </Tooltip>
                )}
              </Box>
            )}

          </Box>
          <Chip 
            label={dataset.type} 
            icon={
                dataset.type === 'Tabular' ? <TabularIcon size={15} /> :
                dataset.type === 'Imaging' ? <ImagingClassificationIcon size={15} /> :
                dataset.type === 'Segmentation' ? <ImagingSegmentationIcon size={15} /> :
                null
            }
            size="small" 
            sx={{ 
              bgcolor: 'var(--fx-info-soft)', 
              color: 'var(--fx-info-text)', 
              fontWeight: 700, 
              fontSize: '0.7rem', 
              borderRadius: '999px',
              height: '24px',
              border: '1px solid #cffafe',
              flexShrink: 0,
              '& .MuiChip-icon': {
                color: 'inherit'
              }
            }} 
          />
        </Box>

        {/* Stats Grid - Light Gray Data Block */}
        <Box sx={{ mb: 2.5, bgcolor: 'var(--fx-subtle)', p: 2, borderRadius: 2, border: '1px solid var(--fx-subtle)' }}>
           <Grid container spacing={2}>
               <Grid item xs={6}>
                   <Box sx={{ borderRight: '1px solid var(--fx-border)', pr: 2 }}>
                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                           <PeopleAltOutlinedIcon sx={{ fontSize: 14, color: 'var(--fx-text-muted)' }} />
                           <Typography variant="caption" sx={{ color: 'var(--fx-text-secondary)', fontWeight: 500, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                               Samples
                           </Typography>
                       </Box>
                       <Typography variant="body2" sx={{ fontFamily: 'var(--fx-font-mono)', fontVariantNumeric: 'tabular-nums', color: 'var(--fx-text-secondary)', fontWeight: 600, fontSize: '0.95rem' }}>
                           {dataset.samples}
                       </Typography>
                   </Box>
               </Grid>
               <Grid item xs={6}>
                   <Box sx={{ pl: 1 }}>
                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                           <StorageOutlinedIcon sx={{ fontSize: 14, color: 'var(--fx-text-muted)' }} />
                           <Typography variant="caption" sx={{ color: 'var(--fx-text-secondary)', fontWeight: 500, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                               Size
                           </Typography>
                       </Box>
                       <Typography variant="body2" sx={{ fontFamily: 'var(--fx-font-mono)', fontVariantNumeric: 'tabular-nums', color: 'var(--fx-text-secondary)', fontWeight: 600, fontSize: '0.95rem' }}>
                           {dataset.size}
                       </Typography>
                   </Box>
               </Grid>
               <Grid item xs={6}>
                   <Box sx={{ borderRight: '1px solid var(--fx-border)', pr: 2 }}>
                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                           <DescriptionOutlinedIcon sx={{ fontSize: 14, color: 'var(--fx-text-muted)' }} />
                           <Typography variant="caption" sx={{ color: 'var(--fx-text-secondary)', fontWeight: 500, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                               Features
                           </Typography>
                       </Box>
                       <Typography variant="body2" sx={{ fontFamily: 'var(--fx-font-mono)', fontVariantNumeric: 'tabular-nums', color: 'var(--fx-text-secondary)', fontWeight: 600, fontSize: '0.95rem' }}>
                           {dataset.features}
                       </Typography>
                   </Box>
               </Grid>
               <Grid item xs={6}>
                    <Box sx={{ pl: 1 }}>
                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                           <PolicyOutlinedIcon sx={{ fontSize: 14, color: 'var(--fx-text-muted)' }} />
                           <Typography variant="caption" sx={{ color: 'var(--fx-text-secondary)', fontWeight: 500, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                               License
                           </Typography>
                       </Box>
                       <Typography variant="body2" sx={{ fontFamily: 'var(--fx-font-mono)', fontVariantNumeric: 'tabular-nums', color: 'var(--fx-text-secondary)', fontWeight: 600, fontSize: '0.95rem' }}>
                           {dataset.license}
                       </Typography>
                   </Box>
               </Grid>
           </Grid>
        </Box>

        {/* Target Variable Display */}
        {dataset.target && (
          <Box sx={{ 
            mb: 2, 
            pl: 2, 
            py: 1,
            borderLeft: '3px solid #06b6d4', 
            bgcolor: 'var(--fx-subtle)',
            borderRadius: '0 4px 4px 0'
          }}>
             <Typography variant="subtitle2" sx={{ color: 'var(--fx-text-strong)', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.85rem' }}>
                <ModelTrainingIcon sx={{ fontSize: 14, color: '#06b6d4' }} /> Target Variable
             </Typography>
             <Typography variant="body2" sx={{ color: 'var(--fx-text-secondary)', mt: 0.5, fontSize: '0.9rem' }}>
                {dataset.target}
             </Typography>
          </Box>
        )}

        {/* Benchmarks / Models Run */}
        {dataset.benchmarks && dataset.benchmarks.length > 0 && (
             <Box sx={{ mb: 2 }}>
                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <HistoryIcon sx={{ fontSize: 14, color: 'var(--fx-text-muted)' }} />
                     <Typography variant="caption" sx={{ color: 'var(--fx-text-secondary)', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Benchmarks & Models
                    </Typography>
                 </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {dataset.benchmarks.map((bm, idx) => {
                        const match = bm.match(/^(.*?) \((\d+) ep\)$/);
                        const tooltipText = match 
                            ? `Model Architecture: ${match[1]} | Training Epochs: ${match[2]}`
                            : `Benchmark Model: ${bm}`;

                        return (
                            <Tooltip key={idx} title={tooltipText} placement="top">
                                <Chip 
                                    label={bm}
                                    size="small"
                                    sx={{ 
                                        height: '20px', 
                                        fontSize: '0.65rem', 
                                        bgcolor: 'var(--fx-subtle)', 
                                        color: 'var(--fx-text-secondary)',
                                        border: '1px solid var(--fx-border)',
                                        borderRadius: '4px',
                                        cursor: 'help',
                                        '& .MuiChip-label': { px: 1 }
                                    }}
                                />
                            </Tooltip>
                        );
                    })}
                 </Box>
             </Box>
        )}

        {/* Description */}
        <Box sx={{ mb: 3, flex: 1 }}>
            <Tooltip title={dataset.description} placement="top-start">
                <Typography variant="body2" sx={{ 
                    color: 'var(--fx-text-secondary)', 
                    lineHeight: 1.6,
                    display: '-webkit-box', 
                    WebkitLineClamp: expanded ? 'unset' : 3, 
                    WebkitBoxOrient: 'vertical', 
                    overflow: 'hidden',
                    transition: 'all 0.3s',
                    cursor: 'help'
                }}>
                {dataset.description}
                </Typography>
            </Tooltip>
            {dataset.description.length > 250 && (
                <Button 
                    size="small" 
                    onClick={() => setExpanded(!expanded)}
                    sx={{ 
                        p: 0, 
                        minWidth: 'auto', 
                        mt: 0.5, 
                        textTransform: 'none', 
                        color: '#06b6d4', 
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' }
                    }}
                >
                    {expanded ? "Show Less" : "Read More"}
                </Button>
            )}
        </Box>

        {/* Action Buttons */}
        <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2, borderTop: '1px solid var(--fx-subtle)' }}>
          <Box>
           {/* Visualize Button (Left) */}
           {dataset.type === 'Tabular' && (
               <Button 
                   onClick={() => handleOpenModal(dataset.id)}
                   startIcon={<AnalyticsIcon />}
                   sx={{ 
                       textTransform: 'none',
                       border: '1px solid var(--fx-border-strong)', // Outlined style
                       color: 'var(--fx-text-secondary)',
                       borderRadius: '6px',
                       px: 2, py: 0.8, // Padding 6px 12px roughly
                       fontWeight: 500,
                       '&:hover': { bgcolor: 'var(--fx-subtle)', borderColor: 'var(--fx-text-muted)' }
                   }}
               >
                   Visualize
               </Button>
           )}
          </Box>
          
          {/* Select Button (Right) */}
          <Tooltip title={isMaintenance ? "Current under maintenance" : ""} arrow placement="top">
            <span style={{ display: 'inline-block' }}> {/* Wrapper to allow tooltip on disabled button */}
                <Button 
                    variant="contained" 
                    disableElevation
                    onClick={handleSelect}
                    disabled={isMaintenance}
                    sx={{ 
                    bgcolor: '#06b6d4',
                    color: 'white',
                    borderRadius: '8px', 
                    px: 3, 
                    py: 0.8,
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                        bgcolor: '#0891b2'
                    },
                    '&.Mui-disabled': {
                        bgcolor: 'var(--fx-text-muted)',
                        color: 'var(--fx-subtle)'
                    }
                    }}
                >
                    Select Dataset
                </Button>
            </span>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );
};

const Datasets = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();
  const navigate = useNavigate(); // Hook for navigation
  
  const {
    datasets,
    datasetsLoading,
    config,
    displayableFeatures,
    displayableFeaturesLoading,
    selectedFeatures,
    setSelectedFeatures,
    fetchDisplayableFeatures,
    featureVisualizations,
    setFeatureVisualizations,
    generateFeatureVisualizations,
    // F1.7 prototype: enrich dataset cards with owning-site context.
    availableDatasets,
    sites,
  } = useAppContext();

  // Local State
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('name_asc');
  const [modalOpen, setModalOpen] = useState(false);
  const [datasetForModal, setDatasetForModal] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [vizSelectedFeatures, setVizSelectedFeatures] = useState([]);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);


  // Dataset Metadata Definition - EDIT HERE to ensure persistence
  // This dictionary maps filename keys to rich, scientific metadata for the UI.
  const DATASET_METADATA = {
    "dataset_primary_site.csv": {
      name: "TCGA Primary Site Cancer Data",
      type: "Tabular",
      category: "Oncology",
      description: "Clinical dataset from The Cancer Genome Atlas (TCGA) focused on primary tumor sites. Includes comprehensive patient demographics, tumor characteristics, and survival outcomes. Ideal for analyzing survival rates across different primary sites.",
      samples: "11,000+",
      features: "15 Clinical Features",
      size: "2.4 MB",
      license: "Open Access",
      quality: "Verified",
      missingData: "~5%",
      target: "Survival / Tumor Type",
      privacy: "De-identified",
      reb: true,
      institution: "Hospital",
      benchmarks: ["TabNet (500 ep)", "FT-Transformer (1000 ep)"]
    },
    "dataset_therapy_type.csv": {
      name: "TCGA Therapy Response Data",
      type: "Tabular",
      category: "Pharmacology",
      description: "Detailed records of therapeutic interventions and patient responses. Contains data on chemotherapy, immunotherapy, and targeted therapy regimens correlated with patient outcomes.",
      samples: "9,500+",
      features: "12 Treatment Features",
      size: "1.8 MB",
      license: "Open Access",
      quality: "Verified",
      missingData: "< 2%",
      target: "Treatment Efficacy",
      privacy: "De-identified",
      reb: true,
      institution: "Hospital",
      benchmarks: ["Logistic Regression (100 ep)", "MLP (200 ep)"]
    },
    "dataset_treatment.csv": {
      name: "Global Cancer Treatment Registry",
      type: "Tabular",
      category: "Epidemiology",
      description: "Aggregated treatment data covering diverse demographic groups. Focuses on accessibility, treatment types, and long-term follow-up statistics across multiple medical centers.",
      samples: "15,000+",
      features: "20+ Features",
      size: "3.1 MB",
      license: "Restricted",
      quality: "Verified",
      missingData: "~10%",
      target: "Treatment Type",
      privacy: "Synthetic",
      reb: false,
      institution: "Industry",
      benchmarks: ["TabNet (200 ep)", "MLP (500 ep)"]
    },
    "LIDC_cls_patches": {
      name: "LIDC-IDRI Lung Nodule Patches",
      type: "Imaging",
      category: "Medical Imaging",
      description: "A subset of the LIDC-IDRI dataset focusing on classified lung nodule patches. Each patch is annotated for malignancy, enabling the training of robust classification models for early lung cancer detection.",
      samples: "15,000 Patches",
      features: "32x32 px / 1 Channel",
      size: "1.2 GB",
      license: "CC-BY-NC",
      quality: "Verified",
      missingData: "None",
      target: "Malignancy (Benign/Malignant)",
      privacy: "De-identified",
      reb: true,
      institution: "Hospital",
      benchmarks: ["ResNet18 (50 ep)", "ConvNeXt (30 ep)"]
    },
    "LIDC_IDRI_segmentation": {
      name: "LIDC-IDRI Volumetric Segmentation",
      type: "Segmentation",
      category: "Medical Imaging",
      description: "Full thoracic CT scans with radiologist-annotated 3D segmentation masks for lung nodules. The gold standard for developing volumetric segmentation algorithms (e.g., U-Net, V-Net).",
      samples: "1,018 Scans",
      features: "512x512 px / Volumetric",
      size: "57 GB",
      license: "CC-BY-NC",
      quality: "Verified",
      missingData: "None",
      target: "Nodule Masks",
      privacy: "De-identified",
      reb: true,
      institution: "Research",
      benchmarks: ["U-Net (300 ep)", "U-Net++ (150 ep)"]
    },
    "mnist": {
      name: "MNIST Handwritten Digits",
      type: "Imaging",
      category: "Computer Vision",
      description: "The classic benchmark dataset consists of 70,000 grayscale images of handwritten digits. Widely used for testing machine learning algorithms and benchmarking new architectures.",
      samples: "70,000",
      features: "28x28 px / Grayscale",
      size: "11 MB",
      license: "Public Domain",
      quality: "Verified",
      missingData: "None",
      target: "Digit Class (0-9)",
      privacy: "Public",
      reb: false,
      institution: "Research",
      benchmarks: ["Swin Transformer (10 ep)", "ResNet18 (20 ep)"]
    },
    "cifar10": {
      name: "CIFAR-10 Object Recognition",
      type: "Imaging",
      category: "Computer Vision",
      description: "A collection of 60,000 32x32 color images in 10 classes (airplanes, cars, birds, cats, deer, dogs, frogs, horses, ships, and trucks). Used to evaluate performance on generic object recognition.",
      samples: "60,000",
      features: "32x32 px / RGB",
      size: "163 MB",
      license: "MIT",
      quality: "Verified",
      missingData: "None",
      target: "Object Category",
      privacy: "Public",
      reb: false,
      institution: "Research",
      benchmarks: ["ResNet18 (100 ep)", "ConvNeXt (120 ep)"]
    }
  };

  // Helper to parse dataset info with enriched metadata
  const getDatasetInfo = (datasetPath) => {
    // F1.7 prototype: if /datasets is driven by SiteDatasetCapability objects
    // (mock mode), prefer the capability-derived rich info so the card can
    // express the site-local product framing.
    if (Array.isArray(availableDatasets) && availableDatasets.length > 0 &&
        typeof availableDatasets[0] === 'object' && availableDatasets[0]?.capability_id) {
      const cap = availableDatasets.find(
        (c) => c && (c.capability_id === datasetPath || c.dataset_ref === datasetPath)
      );
      if (cap) {
        const owningSite = (sites || []).find((s) => s.site_id === cap.site_id);
        const tasks = cap.tasks_supported || [];
        let uiType = 'Tabular';
        if (cap.dataset_type === 'imaging') {
          uiType = tasks.includes('imaging_segmentation') ? 'Segmentation' : 'Imaging';
        }
        const sampleStr =
          typeof cap.sample_count_estimate === 'number'
            ? cap.sample_count_estimate.toLocaleString()
            : (cap.sample_count_estimate || 'N/A');
        return {
          // Existing UI-required fields:
          id: cap.capability_id,
          name: cap.display_name,
          type: uiType,
          size: typeof cap.sample_count_estimate === 'string' ? cap.sample_count_estimate : 'Unknown',
          category: cap.dataset_type === 'imaging' ? 'Medical Imaging' : 'Structured Data',
          description:
            cap.notes ||
            `Owned by ${owningSite?.display_name || cap.site_id}. Capability ${cap.capability_id}.`,
          samples: sampleStr,
          features: 'N/A',
          license: 'N/A',
          quality: cap.readiness_status === 'ready' ? 'Verified' : 'Pending',
          missingData: 'Unknown',
          target: 'Not set',
          privacy: 'Site-local',
          reb: false,
          institution: owningSite?.organization_display_name || 'Site',
          benchmarks: [],
          // F1.7 site-local additions consumed by DatasetCard:
          owning_site_id: cap.site_id,
          owning_site_display_name: owningSite?.display_name || cap.site_id,
          owning_site_status: owningSite?.status || 'unknown',
          readiness_status: cap.readiness_status,
          access_scope: owningSite?.access_scope || 'self',
          tasks_supported: tasks,
          dataset_ref: cap.dataset_ref,
          capability_id: cap.capability_id,
        };
      }
    }

    // Extract the raw filename from the path
    const rawFilename = datasetPath.includes('/') ? datasetPath.split('/').pop() : datasetPath;

    // Check if we have defined metadata for this file
    const definedMeta = DATASET_METADATA[rawFilename];

    if (definedMeta) {
      return { 
        ...definedMeta, 
        id: datasetPath, // Use the full path as the unique ID
        // Ensure any missing optional fields have defaults if needed
        missingData: definedMeta.missingData || 'Unknown',
      };
    }

    // Fallback logic for unknown datasets
    let type = 'Tabular';
    let size = 'Unknown';
    let category = 'General';
    let description = 'No detailed description available for this dataset.';
    let samples = 'N/A';
    let features = 'N/A';
    let license = 'Unknown';
    let quality = 'Unverified';
    let missingData = 'Unknown';
    let privacy = 'Unknown';
    let reb = false;
    let institution = 'Unknown';
    let benchmarks = [];

    if (rawFilename.includes('segmentation') && !rawFilename.includes('cls_patches')) {
      type = 'Segmentation';
      category = 'Medical Imaging';
      features = 'Volumetric';
    } else if (rawFilename.includes('imaging') || rawFilename.includes('image') || rawFilename.includes('patches')) {
      type = 'Imaging';
      category = 'Computer Vision';
      features = 'Pixel Data';
    } else if (rawFilename.endsWith('.csv') || rawFilename.endsWith('.xlsx')) {
      type = 'Tabular';
      category = 'Structured Data';
      features = 'Tabular Columns';
    }

    return { 
        name: rawFilename, // Fallback to filename as title
        type, 
        size, 
        category, 
        description, 
        id: datasetPath,
        samples, 
        features, 
        license, 
        quality, 
        missingData,
        target: 'Unknown',
        privacy,
        reb,
        institution,
        benchmarks
    };
  };

  // Process and Filter Datasets — Option C: grouped by dataset_ref.
  // Each card on /datasets is one logical dataset; the per-site capability
  // rows live inside the card's expandable section. See:
  // Mock_UI_Notes/2026-05-24_dataset_registry_grouping_model.md
  const groupedDatasets = useMemo(() => {
    let groups = groupCapabilitiesByDatasetRef(availableDatasets, sites);

    // dataset_type filter — a group matches "tabular" via dataset_type,
    // "imaging" / "segmentation" via tasks_supported membership.
    if (filterType !== 'all') {
      groups = groups.filter((g) => {
        if (filterType === 'tabular') return g.dataset_type === 'tabular';
        if (filterType === 'imaging') {
          return g.dataset_type === 'imaging' && g.tasks_supported.includes('imaging_classification');
        }
        if (filterType === 'segmentation') {
          return g.dataset_type === 'imaging' && g.tasks_supported.includes('imaging_segmentation');
        }
        return true;
      });
    }

    // Search: match display_name, dataset_ref, or any participating site's display_name.
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      groups = groups.filter((g) => {
        if (g.display_name && g.display_name.toLowerCase().includes(q)) return true;
        if (g.dataset_ref && g.dataset_ref.toLowerCase().includes(q)) return true;
        return g.capabilities.some((cap) => {
          const site = (sites || []).find((s) => s.site_id === cap.site_id);
          const n = site?.display_name || cap.site_id || '';
          return n.toLowerCase().includes(q);
        });
      });
    }

    // Sort. The legacy chip values name_asc/name_desc/size_asc/size_desc are
    // preserved; "size" is reinterpreted as "number of participating sites"
    // since logical groups don't carry a single byte-size string anymore.
    const sorted = [...groups];
    if (sortBy === 'name_asc') sorted.sort((a, b) => a.display_name.localeCompare(b.display_name));
    else if (sortBy === 'name_desc') sorted.sort((a, b) => b.display_name.localeCompare(a.display_name));
    else if (sortBy === 'size_asc') sorted.sort((a, b) => a.sites_count - b.sites_count);
    else if (sortBy === 'size_desc') sorted.sort((a, b) => b.sites_count - a.sites_count);

    return sorted;
  }, [availableDatasets, sites, filterType, searchQuery, sortBy]);

  // Modal Handlers
  const handleOpenModal = async (datasetId) => {
    setDatasetForModal(getDatasetInfo(datasetId));
    setModalOpen(true);
    setFeatureVisualizations(null);
    setVizSelectedFeatures([]); 
    await fetchDisplayableFeatures(datasetId);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setDatasetForModal(null);
  };

  // Helper to generate mock data if backend fails/is missing
  const generateMockVisualizations = (features) => {
      const summary = {
          num_sample: 15420,
          num_feature: features.length,
          visualizations: features.map(f => {
              // Generate realistic-looking distribution
              const isCat = f.toLowerCase().includes('status') || f.toLowerCase().includes('type');
              const bins = isCat 
                  ? ['Category A', 'Category B', 'Category C', 'Category D', 'Other']
                  : ['0-10', '10-20', '20-30', '30-40', '40-50', '50-60', '60-70', '70-80'];
                  
              const counts = bins.map(() => Math.floor(Math.random() * 500) + 50);
              
              return {
                  feature_name: f,
                  chart_type: 'histogram',
                  data: {
                      bins: bins,
                      counts: counts
                  }
              };
          })
      };
      return summary;
  };

  const handleGenerateInModal = async () => {
    if (!datasetForModal || vizSelectedFeatures.length === 0) return;
    setIsGenerating(true);
    try {
      // Try backend first
      const data = await generateFeatureVisualizations(vizSelectedFeatures, datasetForModal.id);
      
      // Fallback to mock if backend returns incomplete data (e.g. during dev)
      if (!data || !data.visualizations || data.visualizations.length === 0) {
           console.warn("Backend returned no visualizations, falling back to mock data.");
           const mockData = generateMockVisualizations(vizSelectedFeatures);
           setFeatureVisualizations(mockData);
      }
    } catch (e) {
      console.error("Backend generation failed, using mock data:", e);
      const mockData = generateMockVisualizations(vizSelectedFeatures);
      setFeatureVisualizations(mockData);
    } finally {
      setIsGenerating(false);
    }
  };

  // Calculate counts at the LOGICAL DATASET level (Option C). A group with
  // both classification + segmentation tasks counts in both filter chips —
  // accurate to how those chips behave.
  const datasetCounts = useMemo(() => {
    const all = groupCapabilitiesByDatasetRef(availableDatasets, sites);
    const counts = { all: all.length, tabular: 0, imaging: 0, segmentation: 0 };
    for (const g of all) {
      if (g.dataset_type === 'tabular') counts.tabular++;
      if (g.dataset_type === 'imaging') {
        if (g.tasks_supported.includes('imaging_classification')) counts.imaging++;
        if (g.tasks_supported.includes('imaging_segmentation')) counts.segmentation++;
      }
    }
    return counts;
  }, [availableDatasets, sites]);

  // Page-level filter chips. Labels follow the task taxonomy used on each
  // DatasetGroupCard: no standalone "Imaging" — imaging tasks are always
  // qualified as "Imaging · Classification" or "Imaging · Segmentation".
  // The `value` strings stay the same so the existing filter logic in
  // `groupedDatasets` (which keys on `tabular` / `imaging` / `segmentation`)
  // is unchanged.
  const filterChips = [
     { label: 'All Datasets', value: 'all', count: datasetCounts.all, icon: <AppsIcon fontSize="small" /> },
     { label: 'Tabular', value: 'tabular', count: datasetCounts.tabular, icon: <TabularIcon /> },
     { label: 'Imaging · Classification', value: 'imaging', count: datasetCounts.imaging, icon: <ImagingClassificationIcon /> },
     { label: 'Imaging · Segmentation', value: 'segmentation', count: datasetCounts.segmentation, icon: <ImagingSegmentationIcon /> },
  ];

  return (
    <Box sx={{ p: 3, maxWidth: 1440, mx: 'auto' }}>
       {/* Header */}
       <HeaderWithControls
         icon={<DatasetOutlinedIcon />}
         title={
           <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
             {t('datasets.title')}
              <Tooltip 
                 title={<OmopInfoCard />} 
                 arrow 
                 placement="bottom"
                 componentsProps={{
                     tooltip: {
                         sx: {
                             bgcolor: colors.grey[800],
                             border: `1px solid ${colors.grey[700]}`,
                             p: 0,
                             '& .MuiTooltip-arrow': {
                                 color: colors.grey[800],
                             }
                         }
                     }
                 }}
              >
                  <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: colors.secondary[400],
                      cursor: 'help',
                      transition: 'color 0.2s',
                      '&:hover': {
                          color: colors.secondary[500],
                      }
                  }}>
                       <VerifiedUserOutlinedIcon sx={{ fontSize: 15 }} />
                       <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.78rem', lineHeight: 1, letterSpacing: 0.2 }}>
                          Standardized Schema
                       </Typography>
                  </Box>
              </Tooltip>
           </Box>
         }
         subtitle="Organize and analyze your federated learning datasets."
       />

       {/* Unified one-line toolbar: filters + count · search + sort + upload */}
       <PageToolbar
          tabs={filterChips}
          filterValue={filterType}
          onFilterChange={setFilterType}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          searchPlaceholder="Search datasets..."
          sortBy={sortBy}
          setSortBy={setSortBy}
          sortOptions={[
             <MenuItem key="name_asc" value="name_asc">Name (A-Z)</MenuItem>,
             <MenuItem key="name_desc" value="name_desc">Name (Z-A)</MenuItem>,
             <MenuItem key="size_asc" value="size_asc">Size (Small-Large)</MenuItem>,
             <MenuItem key="size_desc" value="size_desc">Size (Large-Small)</MenuItem>
          ]}
          countText={`Showing ${groupedDatasets.length} logical ${groupedDatasets.length === 1 ? 'dataset' : 'datasets'}`}
          rightExtra={
             <Button
                 variant="contained"
                 startIcon={<CloudUploadIcon />}
                 onClick={() => setUploadModalOpen(true)}
                 sx={{
                     height: 38,
                     px: 2,
                     borderRadius: 2,
                 }}
             >
                 Upload Dataset
             </Button>
          }
       />

        {/* Grid Content — Option C grouped cards (one per dataset_ref) */}
        {datasetsLoading ? (
          <LoadingState label="Loading datasets…" minHeight={220} />
        ) : (
          <Grid container spacing={3}>
            {groupedDatasets.length > 0 ? groupedDatasets.map((group) => (
              <Grid item xs={12} sm={6} md={4} key={group.dataset_ref}>
                <DatasetGroupCard group={group} />
              </Grid>
            )) : (
              <Grid item xs={12}>
                <EmptyState
                  icon={<StorageOutlinedIcon sx={{ fontSize: 22 }} />}
                  title="No datasets found"
                  description="No datasets match your current search or filters. Try adjusting your criteria."
                  minHeight={160}
                />
              </Grid>
            )}
          </Grid>
        )}

        {/* Visualization Modal */}
        <VisualizationModal 
          open={modalOpen} 
          onClose={handleCloseModal} 
          dataset={datasetForModal}
          isGenerating={isGenerating}
          onGenerate={handleGenerateInModal}
          displayableFeatures={displayableFeatures}
         displayableFeaturesLoading={displayableFeaturesLoading}
         selectedFeatures={vizSelectedFeatures}
         setSelectedFeatures={setVizSelectedFeatures}
         onFeatureToggle={(feature, checked) => {
           const newSelected = checked 
               ? [...vizSelectedFeatures, feature] 
               : vizSelectedFeatures.filter(f => f !== feature);
           setVizSelectedFeatures(newSelected);
         }}
         onSelectAll={(checked) => {
           setVizSelectedFeatures(checked ? displayableFeatures : []);
         }}
         visualizations={
            // Mock visualization data if none exists yet, for testing layout
            featureVisualizations
         }
       />
      
      {/* Upload Dataset Modal */}
      <UploadDatasetModal 
        open={uploadModalOpen} 
        onClose={() => setUploadModalOpen(false)} 
      />
    </Box>
  );
};

export default Datasets;